import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ArrowRight, Menu, X,
  Linkedin, Instagram, Twitter, Youtube,
  ChevronDown, Heart, Brain, HeartHandshake, Globe, Gift,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

// ─── Social Links ──────────────────────────────────────────────────────────────
const SOCIAL_LINKS = [
  { icon: Linkedin,  href: 'https://www.linkedin.com/company/soulsathiya/', label: 'LinkedIn'  },
  { icon: Instagram, href: 'https://instagram.com/soulsathiya',        label: 'Instagram' },
  { icon: Twitter,   href: 'https://x.com/soul_sathiya',          label: 'Twitter/X' },
  { icon: Youtube,   href: 'https://youtube.com/@soulsathiya',         label: 'YouTube'   },
];

// ─── Brand Logo ────────────────────────────────────────────────────────────────
// Figma lockup (Layer_1 / Vector, 199.589 × 37.903): S-swirl mark + "SoulSathiya" wordmark in one gradient SVG.
const SoulSathiyaLogo = ({ className = 'h-[38px] w-auto', style }) => (
  <img src="/logo-lockup.svg" alt="SoulSathiya" className={`${className} object-contain`} style={style} draggable={false} />
);

// ─── 4 Product Verticals ───────────────────────────────────────────────────────
const PRODUCTS = [
  {
    label: 'Intentional Matchmaking',
    desc:  'Psychology-based matching for singles ready for commitment',
    to:    '/intentional-matchmaking',
    live:  true,
    icon:  Heart,
    color: 'hsl(43,82%,52%)',
  },
  {
    label: 'Relationship Intelligence',
    desc:  'The Couple Compatibility Report, before you commit',
    to:    '/relationship-intelligence',
    live:  true,
    icon:  Brain,
    color: 'hsl(217,91%,60%)',
  },
  {
    label: 'Relationship Wellness',
    desc:  'Expert counselling for your relationship, opening soon',
    to:    '/care',
    live:  true,   // stays a link: /care holds the waitlist and the expert application
    soon:  true,   // but carries a "Coming soon" tag until Care opens
    icon:  HeartHandshake,
    color: 'hsl(142,71%,45%)',
  },
  {
    label: 'Relationship Ecosystem',
    desc:  'Complete matrimony & relationship support network',
    to:    '/ecosystem',
    live:  false,
    icon:  Globe,
    color: 'hsl(280,70%,60%)',
  },
];

// ─── Other Nav Links ───────────────────────────────────────────────────────────
const OTHER_LINKS = [
  { label: 'How It Works', to: '/how-it-works' },
  { label: 'About Us',     to: '/about'        },
  { label: 'FAQ',          to: '/faq'          },
  { label: 'Blogs',        to: '/blogs'        },
];

// ─── Products Dropdown (desktop) ──────────────────────────────────────────────
function ProductsDropdown({ location }) {
  const [open, setOpen]   = useState(false);
  const timerRef          = useRef(null);

  const openMenu  = () => { clearTimeout(timerRef.current); setOpen(true);  };
  const closeMenu = () => { timerRef.current = setTimeout(() => setOpen(false), 160); };

  const isProductActive =
    location.pathname === '/intentional-matchmaking' ||
    location.pathname === '/relationship-intelligence' ||
    location.pathname.startsWith('/v2/couple') ||
    location.pathname === '/care' ||
    location.pathname === '/ecosystem';

  return (
    <div className="relative" onMouseEnter={openMenu} onMouseLeave={closeMenu}>

      {/* Trigger */}
      <button
        className={`flex items-center gap-1 py-1 text-sm font-medium transition-colors duration-200 relative select-none ${
          isProductActive
            ? 'text-primary font-semibold'
            : 'text-muted-foreground hover:text-foreground'
        }`}
      >
        Products
        <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
        {isProductActive && (
          <span className="absolute -bottom-0.5 left-0 right-6 h-0.5 bg-primary rounded-full" />
        )}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div
          className="absolute top-full left-1/2 -translate-x-1/2 mt-2.5 w-80 rounded-2xl border border-border/50 shadow-2xl shadow-black/40 overflow-hidden z-50"
          style={{ background: 'hsl(225,38%,9%)' }}
        >
          <div className="p-2 space-y-0.5">
            {PRODUCTS.map(({ label, desc, to, live, soon, icon: Icon, color }) => {
              const active =
                location.pathname === to ||
                (to === '/relationship-intelligence' && location.pathname.startsWith('/v2/couple'));

              return live ? (
                <Link
                  key={label}
                  to={to}
                  className={`flex items-start gap-3 p-3 rounded-xl transition-all duration-150 group ${
                    active ? 'bg-primary/10' : 'hover:bg-white/5'
                  }`}
                >
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ background: `${color}18`, border: `1px solid ${color}30` }}
                  >
                    <Icon className="w-4 h-4" style={{ color }} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className={`text-sm font-semibold leading-snug transition-colors ${
                        active ? 'text-primary' : 'text-foreground group-hover:text-primary'
                      }`}>
                        {label}
                      </p>
                      {soon && (
                        <span className="text-[9px] font-bold uppercase tracking-widest text-primary bg-primary/15 px-1.5 py-0.5 rounded-full">
                          Coming soon
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{desc}</p>
                  </div>
                </Link>
              ) : (
                <div
                  key={label}
                  className="flex items-start gap-3 p-3 rounded-xl opacity-45 cursor-not-allowed select-none"
                >
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ background: `${color}10`, border: `1px solid ${color}20` }}
                  >
                    <Icon className="w-4 h-4" style={{ color }} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold text-foreground leading-snug">{label}</p>
                      <span className="text-[9px] font-bold uppercase tracking-widest text-primary bg-primary/15 px-1.5 py-0.5 rounded-full">
                        Soon
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{desc}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer strip */}
          <div className="border-t border-border/25 px-4 py-2 bg-white/2">
            <p className="text-[10px] text-muted-foreground/60 uppercase tracking-widest">
              AI-Powered · Psychologically Grounded
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Navbar ────────────────────────────────────────────────────────────────────
const Navbar = ({ transparent = false }) => {
  const location             = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  const [scrolled,   setScrolled]   = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    setIsLoggedIn(!!localStorage.getItem('session_token'));
  }, [location]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setMobileProductsOpen(false);
    // ScrollToTop (App.js) owns scroll restoration; only reset here when there is
    // no hash, so anchor links like /#testimonials can land on their section.
    if (!location.hash) window.scrollTo({ top: 0 });
  }, [location.pathname, location.hash]);

  // At rest the header sits flush on the page — no panel, no seam — so the nav
  // and the hero below it read as one surface on every page (matching the
  // landing page). Once scrolled it earns a glass surface so the links stay
  // legible over whatever content is passing underneath.
  const isOverlay = !scrolled;

  return (
    <header
      data-site-nav
      className={`${transparent ? 'fixed' : 'sticky'} top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isOverlay
          ? 'bg-transparent border-b border-transparent'
          : 'glass-card border-b border-border/50 backdrop-blur-md shadow-lg shadow-black/20'
      }`}
      // On an iPhone (installed app, or any screen with a notch) the page runs
      // under the status bar (viewport-fit=cover). The hero may; the logo and
      // the menu may not — they sat behind the clock (founder, Oct 7 2026).
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
    >
      {/* Transparent-overlay pages put the gold lockup over whatever the hero
          photo happens to be (the homepage hero is bright gold). A soft scrim
          keeps the logo and menu legible without reintroducing a hard band. */}
      {isOverlay && (
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'linear-gradient(180deg, rgba(8,17,31,0.55) 0%, rgba(8,17,31,0.28) 55%, rgba(8,17,31,0) 100%)' }}
        />
      )}
      <div className="container mx-auto px-5 sm:px-6 py-5 sm:py-4 flex items-center justify-between relative">

        {/* ── Logo ── */}
        {/* On mobile the gold lockup sits over a bright gold hero photo, so it
            needs both a smaller footprint (it was 200px on a 390px screen) and
            a drop shadow to stay legible against whatever is behind it. */}
        <Link
          to="/"
          className="flex items-center space-x-2.5 group"
          onClick={() => setMobileOpen(false)}
        >
          <SoulSathiyaLogo
            className="h-[30px] sm:h-[38px] w-auto transition-transform duration-200 group-hover:scale-105"
            style={{ filter: 'drop-shadow(0 1px 6px rgba(8,17,31,0.75))' }}
          />
        </Link>

        {/* ── Desktop Nav ── */}
        <nav className="hidden md:flex items-center space-x-7 text-sm font-medium">
          <ProductsDropdown location={location} />
          {OTHER_LINKS.map(({ label, to }) => {
            const isActive = location.pathname === to;
            return (
              <Link
                key={to}
                to={to}
                aria-current={isActive ? 'page' : undefined}
                className={`ss-navlink py-1 transition-colors duration-200 ${
                  isActive ? 'text-primary font-semibold' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {label}
              </Link>
            );
          })}
        </nav>

        {/* ── Desktop CTA ── */}
        <div className="hidden md:flex items-center gap-2">
          {isLoggedIn ? (
            <>
              <Link
                to="/refer"
                className={`ss-navlink flex items-center gap-1.5 px-3 py-1 text-sm font-medium transition-colors duration-200 ${
                  location.pathname === '/refer' ? 'text-primary font-semibold' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Gift className="w-4 h-4" />
                Invite a friend
              </Link>
              <Link to="/dashboard">
                <Button size="sm" className="btn-primary px-5 shadow-lg shadow-primary/20 font-semibold">
                  Dashboard
                  <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </Link>
            </>
          ) : (
            <Link to="/login">
              <Button size="sm" className="btn-primary px-5 shadow-lg shadow-primary/20 font-semibold">
                Login
                <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </Link>
          )}
        </div>

        {/* ── Mobile Hamburger ── */}
        <button
          className="md:hidden text-foreground p-2 rounded-lg hover:bg-white/10 transition-colors"
          onClick={() => setMobileOpen(prev => !prev)}
          aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
        >
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* ── Mobile Drawer ── */}
      {mobileOpen && (
        <div className="md:hidden glass-card border-t border-border/40 px-6 py-5 space-y-1 ss-menu-in">

          {/* Products accordion */}
          <div>
            <button
              onClick={() => setMobileProductsOpen(prev => !prev)}
              className="flex items-center justify-between w-full text-base font-medium py-3 border-b border-border/20 text-muted-foreground hover:text-foreground transition-colors"
            >
              Products
              <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${mobileProductsOpen ? 'rotate-180' : ''}`} />
            </button>
            {mobileProductsOpen && (
              <div className="py-2 space-y-1">
                {PRODUCTS.map(({ label, to, live, soon, icon: Icon, color }) =>
                  live ? (
                    <Link
                      key={label}
                      to={to}
                      className={`flex items-center gap-3 px-2 py-2.5 rounded-xl transition-colors ${
                        location.pathname === to ? 'text-primary bg-primary/8' : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ background: `${color}15`, border: `1px solid ${color}25` }}>
                        <Icon className="w-3.5 h-3.5" style={{ color }} />
                      </div>
                      <span className="text-sm font-medium">{label}</span>
                      {soon && (
                        <span className="text-[9px] font-bold uppercase tracking-widest text-primary bg-primary/15 px-1.5 py-0.5 rounded-full ml-auto">
                          Coming soon
                        </span>
                      )}
                    </Link>
                  ) : (
                    <div
                      key={label}
                      className="flex items-center gap-3 px-2 py-2.5 rounded-xl opacity-40 cursor-not-allowed"
                    >
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ background: `${color}10` }}>
                        <Icon className="w-3.5 h-3.5" style={{ color }} />
                      </div>
                      <span className="text-sm font-medium text-foreground">{label}</span>
                      <span className="text-[9px] font-bold uppercase tracking-widest text-primary bg-primary/15 px-1.5 py-0.5 rounded-full ml-auto">
                        Soon
                      </span>
                    </div>
                  )
                )}
              </div>
            )}
          </div>

          {/* Other links */}
          {OTHER_LINKS.map(({ label, to }) => (
            <Link
              key={to}
              to={to}
              className={`block text-base font-medium py-3 border-b border-border/20 transition-colors ${
                location.pathname === to ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {label}
            </Link>
          ))}

          <div className="pt-3 space-y-3">
            {isLoggedIn ? (
              <>
                <Link
                  to="/refer"
                  className={`flex items-center gap-2 text-base font-medium py-1 transition-colors ${
                    location.pathname === '/refer' ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Gift className="w-4 h-4" />
                  Invite a friend
                </Link>
                <Link to="/dashboard" className="block">
                  <Button size="sm" className="btn-primary w-full font-semibold">
                    Dashboard
                    <ArrowRight className="w-4 h-4 ml-1.5" />
                  </Button>
                </Link>
              </>
            ) : (
              <>
                <Link to="/login" className="block text-base text-muted-foreground hover:text-foreground py-1 transition-colors">
                  Login
                </Link>
                <Link to="/register" className="block">
                  <Button size="sm" className="btn-primary w-full font-semibold">
                    Create Account
                    <ArrowRight className="w-4 h-4 ml-1.5" />
                  </Button>
                </Link>
              </>
            )}
            <div className="flex items-center gap-3 pt-2">
              {SOCIAL_LINKS.map(({ icon: Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  aria-label={label}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-full bg-white/10 border border-white/25 flex items-center justify-center text-white/80 hover:bg-white/20 hover:text-white transition-all duration-200"
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
